import type { ApolloError } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import {
  CallPlanInfoFragment,
  CreateCallPlanMutation,
  CreateCallPlanMutationVariables,
  GetActivitiesQuery,
  GetCallPlanActivitiesQuery,
  GetCallPlanActivitiesQueryVariables,
  UpdateCallPlanMutation,
  UpdateCallPlanMutationVariables,
} from "@/graphql/graphql";
import { addCalendarDays, toEndDateTimeUtc, toStartDateTimeUtc } from "@/utils/date-utils";
import { formatGqlExecutionErrors, graphqlClientErrorMessage } from "@/utils/format";

import { ActivitiesTableWithData, type ActivitiesTableWithDataRef } from "./ActivitiesTable";
import {
  CREATE_CALL_PLAN_MUTATION,
  UPDATE_CALL_PLAN_MUTATION,
} from "./CreateCallPlanModal.mutations";
import { GET_CALL_PLAN_ACTIVITIES_QUERY } from "./CreateCallPlanModal.queries";
import {
  callPlanActivitiesHeadingSx,
  callPlanFieldsSx,
  callPlanProgramWeekBlockSx,
  callPlanProgramWeekCaptionSx,
  callPlanProgramWeekValueSx,
  modalCloseGlyphSx,
  modalCloseIconButtonSx,
  modalContainerSx,
  modalFooterBarSx,
  modalFormSx,
  modalHeaderSx,
  modalMainColumnSx,
  modalTitleSx,
  selectedActivitiesListScrollSx,
  selectedActivitiesSectionSx,
} from "./CreateCallPlanModal.styles";
import {
  CALL_PLAN_DURATION_DAYS,
  CallPlanFormValues,
  callPlanInteractionsToPlanActivityRows,
  computeDefaults,
  computeEditFormValues,
  CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE,
  getCallPlanSubmitButtonLabel,
  getNextProgramWeekDisplay,
  MAX_CALL_PLAN_ACTIVITIES,
  Program,
  validateEndDate,
  validateStartDate,
} from "./CreateCallPlanModal.utils";
import SelectedActivitiesList from "./SelectedActivitiesList";

type PlanActivityRow = GetActivitiesQuery["activities"]["activities"][number];
interface CreateCallPlanModalProps {
  accountId: number;
  callPlans: CallPlanInfoFragment[];
  activeProgram?: Program | null;
  /** When set, the modal opens in edit mode for this plan. */
  callPlanToEdit?: CallPlanInfoFragment | null;
  open: boolean;
  onClose: () => void;
}

const CreateCallPlanModal: React.FC<CreateCallPlanModalProps> = ({
  accountId,
  callPlans,
  activeProgram,
  callPlanToEdit,
  open,
  onClose,
}) => {
  const [showError, setShowError] = useState(false);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  /** Suppress re-opening the error snackbar from the Apollo sync effect until errors clear or the modal closes. */
  const errorSnackbarUserDismissedRef = useRef(false);
  const activitiesListRef = useRef<ActivitiesTableWithDataRef | null>(null);
  const [listQueryErrors, setListQueryErrors] = useState<{
    activitiesError?: ApolloError;
    activityTypesError?: ApolloError;
  }>({});

  const handleListQueryErrors = useCallback(
    (errors: { activitiesError?: ApolloError; activityTypesError?: ApolloError }) => {
      setListQueryErrors(errors);
    },
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors: fieldErrors },
  } = useForm<CallPlanFormValues>({ mode: "onChange" });

  const watchedStartDate = watch("startDate");
  const watchedEndDate = watch("endDate");

  const programWeekDisplay = useMemo(() => {
    if (callPlanToEdit) {
      return callPlanToEdit.programWeek;
    }

    return getNextProgramWeekDisplay(callPlans, activeProgram);
  }, [callPlanToEdit, callPlans, activeProgram]);

  const createBlockedByProgramEnd = !callPlanToEdit && !activeProgram;

  const callPlansForOverlap = useMemo(
    () => (callPlanToEdit ? callPlans.filter((cp) => cp.id !== callPlanToEdit.id) : callPlans),
    [callPlans, callPlanToEdit]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      callPlanToEdit
        ? computeEditFormValues(callPlanToEdit)
        : computeDefaults(callPlans, activeProgram)
    );
  }, [open, callPlans, callPlanToEdit, reset, activeProgram]);

  useEffect(() => {
    if (!open) {
      return;
    }

    trigger(["startDate", "endDate"]);
  }, [open, watchedStartDate, watchedEndDate, callPlansForOverlap, trigger]);

  const [createCallPlan, { loading: createLoading, error: createError }] = useMutation<
    CreateCallPlanMutation,
    CreateCallPlanMutationVariables
  >(CREATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });
  const [updateCallPlan, { loading: updateLoading, error: updateError }] = useMutation<
    UpdateCallPlanMutation,
    UpdateCallPlanMutationVariables
  >(UPDATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });
  const [selectedPlanActivities, setSelectedPlanActivities] = useState<PlanActivityRow[]>([]);

  const selectedPlanActivityIds = useMemo(
    () => Object.fromEntries(selectedPlanActivities.map((a) => [a.id, true])),
    [selectedPlanActivities]
  );

  const { data: callPlanActivitiesData, error: callPlanActivitiesError } = useQuery<
    GetCallPlanActivitiesQuery,
    GetCallPlanActivitiesQueryVariables
  >(GET_CALL_PLAN_ACTIVITIES_QUERY, {
    skip: !open || !callPlanToEdit,
    variables: callPlanToEdit ? { callPlanId: callPlanToEdit.id } : undefined,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!open) {
      setSelectedPlanActivities([]);
      setShowError(false);
      setErrorNotice(null);
      errorSnackbarUserDismissedRef.current = false;
      return;
    }

    const apolloErr =
      createError ??
      updateError ??
      callPlanActivitiesError ??
      listQueryErrors.activitiesError ??
      listQueryErrors.activityTypesError;

    if (!apolloErr) {
      errorSnackbarUserDismissedRef.current = false;
      return;
    }

    if (errorSnackbarUserDismissedRef.current) {
      return;
    }

    const fallback =
      createError ?? updateError
        ? "Could not save the call plan."
        : "Could not load activities or filter options.";
    setErrorNotice(graphqlClientErrorMessage(apolloErr, fallback));
    setShowError(true);
  }, [
    callPlanActivitiesError,
    createError,
    listQueryErrors.activitiesError,
    listQueryErrors.activityTypesError,
    open,
    updateError,
  ]);

  useEffect(() => {
    if (!open || !callPlanToEdit) {
      return;
    }

    const plan = callPlanActivitiesData?.callPlan;
    if (!plan || plan.id !== callPlanToEdit.id) {
      return;
    }

    setSelectedPlanActivities(callPlanInteractionsToPlanActivityRows(plan.interactions));
  }, [open, callPlanToEdit, callPlanActivitiesData]);

  const addActivityToPlan = (activity: PlanActivityRow) => {
    setSelectedPlanActivities((prev) => {
      if (prev.length >= MAX_CALL_PLAN_ACTIVITIES || prev.some((a) => a.id === activity.id)) {
        return prev;
      }

      return [...prev, activity];
    });
  };

  const removeActivityFromPlan = (activityId: number) => {
    setSelectedPlanActivities((prev) => prev.filter((a) => a.id !== activityId));
  };

  const startDateRegistration = register("startDate", {
    required: "Start date is required.",
    validate: (v) =>
      validateStartDate(
        v,
        getValues("endDate"),
        callPlansForOverlap,
        activeProgram,
        callPlanToEdit?.isActive
      ),
  });
  const { onChange: onStartDateChange, ...startDateRegistrationRest } = startDateRegistration;

  const completeCallPlanSaveSuccess = async (notice: string) => {
    activitiesListRef.current?.resetList();
    setSuccessNotice(notice);
    setShowSuccess(true);
    onClose();
  };

  const reportCallPlanSaveError = (notice: string) => {
    errorSnackbarUserDismissedRef.current = false;
    setErrorNotice(notice);
    setShowError(true);
  };

  const submitCreateCallPlan = async (values: CallPlanFormValues) => {
    try {
      const result = await createCallPlan({
        variables: {
          input: {
            accountId,
            activityIds: selectedPlanActivities.map((a) => a.id),
            startDate: toStartDateTimeUtc(values.startDate),
            endDate: toEndDateTimeUtc(values.endDate),
          },
        },
      });
      if (!result.data?.createCallPlan) {
        return reportCallPlanSaveError(
          formatGqlExecutionErrors(result) || "Could not create the call plan."
        );
      }

      await completeCallPlanSaveSuccess("Call plan created successfully.");
    } catch (e) {
      reportCallPlanSaveError(graphqlClientErrorMessage(e, "Could not create the call plan."));
    }
  };

  const submitUpdateCallPlan = async (values: CallPlanFormValues) => {
    const plan = callPlanToEdit;
    if (!plan) {
      return;
    }
    try {
      const result = await updateCallPlan({
        variables: {
          input: {
            callPlanId: plan.id,
            activityIds: selectedPlanActivities.map((a) => a.id),
            startDate: toStartDateTimeUtc(values.startDate),
            endDate: toEndDateTimeUtc(values.endDate),
          },
        },
      });
      if (!result.data?.updateCallPlan) {
        return reportCallPlanSaveError(
          formatGqlExecutionErrors(result) || "Could not update the call plan."
        );
      }

      await completeCallPlanSaveSuccess("Call plan updated successfully.");
    } catch (e) {
      reportCallPlanSaveError(graphqlClientErrorMessage(e, "Could not update the call plan."));
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalContainerSx}>
          <Box sx={modalHeaderSx}>
            <Typography variant="h4" sx={modalTitleSx}>
              {callPlanToEdit ? "Edit Call Plan" : "Create Call Plan"}
            </Typography>
            <IconButton
              onClick={onClose}
              aria-label="Close"
              size="small"
              sx={modalCloseIconButtonSx}
            >
              <CloseIcon sx={modalCloseGlyphSx} />
            </IconButton>
          </Box>

          <Box
            component="form"
            noValidate
            sx={modalFormSx}
            onSubmit={handleSubmit(callPlanToEdit ? submitUpdateCallPlan : submitCreateCallPlan)}
          >
            <Box sx={callPlanFieldsSx}>
              <Box sx={callPlanProgramWeekBlockSx}>
                <Typography variant="caption" sx={callPlanProgramWeekCaptionSx}>
                  Program week
                </Typography>
                <Typography variant="body1" sx={callPlanProgramWeekValueSx}>
                  {programWeekDisplay}
                </Typography>
              </Box>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                disabled={callPlanToEdit?.isActive}
                error={!!fieldErrors.startDate}
                helperText={fieldErrors.startDate?.message}
                sx={{ width: 180 }}
                {...startDateRegistrationRest}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) {
                    setValue("endDate", addCalendarDays(v, CALL_PLAN_DURATION_DAYS), {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                  }
                  onStartDateChange(e);
                  if (v) {
                    trigger("endDate");
                  }
                }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!fieldErrors.endDate}
                helperText={fieldErrors.endDate?.message}
                sx={{ width: 180 }}
                {...register("endDate", {
                  required: "End date is required.",
                  validate: (v) =>
                    validateEndDate(
                      v,
                      getValues("startDate"),
                      callPlansForOverlap,
                      activeProgram,
                      callPlanToEdit?.isActive ? callPlanToEdit.startDate : null
                    ),
                })}
              />
            </Box>

            <ActivitiesTableWithData
              ref={activitiesListRef}
              accountId={accountId}
              skip={!open}
              onQueryErrorsChange={handleListQueryErrors}
              mainColumnWrapSx={modalMainColumnSx}
              activityIdsSelected={selectedPlanActivityIds}
              atCapacity={selectedPlanActivities.length >= MAX_CALL_PLAN_ACTIVITIES}
              onAddActivity={addActivityToPlan}
              onRemoveActivity={removeActivityFromPlan}
              endSlot={({ queryState, typeKeysSelected }) => (
                <Box sx={selectedActivitiesSectionSx}>
                  <Typography component="h2" sx={callPlanActivitiesHeadingSx}>
                    {`Call plan activities (${selectedPlanActivities.length}/${MAX_CALL_PLAN_ACTIVITIES})`}
                  </Typography>
                  <Box sx={selectedActivitiesListScrollSx}>
                    <SelectedActivitiesList
                      activities={selectedPlanActivities}
                      onReorder={setSelectedPlanActivities}
                      onRemove={removeActivityFromPlan}
                      queryState={queryState}
                      typeKeysSelected={typeKeysSelected}
                    />
                  </Box>
                </Box>
              )}
            />

            <Box sx={modalFooterBarSx}>
              <Tooltip
                title={createBlockedByProgramEnd && CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE}
              >
                <span>
                  <Button
                    className="button"
                    variant="contained"
                    type="submit"
                    disabled={
                      createLoading ||
                      updateLoading ||
                      createBlockedByProgramEnd ||
                      selectedPlanActivities.length === 0 ||
                      selectedPlanActivities.length > MAX_CALL_PLAN_ACTIVITIES
                    }
                  >
                    {getCallPlanSubmitButtonLabel({
                      createLoading,
                      updateLoading,
                      callPlanToEdit,
                    })}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => {
          errorSnackbarUserDismissedRef.current = true;
          setShowError(false);
        }}
        TransitionProps={{ onExited: () => setErrorNotice(null) }}
      >
        <Alert
          onClose={() => {
            errorSnackbarUserDismissedRef.current = true;
            setShowError(false);
          }}
          severity="error"
        >
          {errorNotice ?? "Something went wrong."}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => {
          setShowSuccess(false);
          setSuccessNotice(null);
        }}
      >
        <Alert
          onClose={() => {
            setShowSuccess(false);
            setSuccessNotice(null);
          }}
          severity="success"
        >
          {successNotice ?? "Call plan saved."}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateCallPlanModal;
